class ProductSelector extends HTMLElement {
  constructor() {
    super();
    this.inputWrapper = '[data-input-wrapper]';
    this.variants = JSON.parse(
      this.querySelector('[type="application/json"]').textContent
    );
    this.form = document.querySelector(
      `#ProductForm-${this.dataset.sectionId}`
    );
    this.purchaseOptions = document.querySelector(
      `#Product-Purchase-Options-${this.dataset.sectionId}`
    );
    this.productBar = document.querySelector(
      `#ProductBar-${this.dataset.sectionId}`
    );
    this.installmentsForm = document.querySelector(
      `#Product-Installments-${this.dataset.installmentsFormId}` // check: `${this.dataset.installmentsFormId}`
    );
    this.addEventListener('change', this.onVariantChange);
    this.updateOptions();
    this.updateVariantStatuses();
    this.updateVariant();

    this.form.toggleAddButton(!this.currentVariant.available, '');
  }

  updateVariantStatuses() {
    // Hound-Tees: <product-selector data-show-soldout> (core range and sold-out products, see
    // snippets/main-product__variant-picker.liquid) keeps sold-out values selectable and
    // labelled sold out, so a shopper can choose their size and sign up for back in stock.
    // An option with a single value keeps its plain label. Values with no variant at all for
    // the current selection stay disabled, and if the selected value is one of them, the first
    // available value (else the first sold-out one) is selected instead, so the picker always
    // lands on a variant that exists (onVariantChange then re-reads the options).
    const keepSoldOut = this.hasAttribute('data-show-soldout');
    const soldOutText =
      (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
    const optionOneValue = this.querySelector(
      this.inputWrapper
    ).querySelector(':checked').value;
    const selectedOptionOneVariants = this.variants.filter(
      variant => optionOneValue === variant.option1
    );
    const inputWrappers = [
      ...this.querySelectorAll(this.inputWrapper)
    ];

    inputWrappers.forEach((option, index) => {
      if (index === 0) return;

      const previousOptionSelected =
        inputWrappers[index - 1].querySelector(':checked').value;
      const matchingVariants = selectedOptionOneVariants.filter(
        variant => variant[`option${index}`] === previousOptionSelected
      );
      const listOfAvailableOptions = matchingVariants
        .filter(variant => variant.available)
        .map(variantOption => variantOption[`option${index + 1}`]);
      const listOfExistingOptions = matchingVariants.map(
        variantOption => variantOption[`option${index + 1}`]
      );

      const optionInputs = [
        ...option.querySelectorAll('input[type="radio"], option')
      ];
      const hasManyValues = optionInputs.length > 1;

      optionInputs.forEach(input => {
        const value = input.getAttribute('value');
        const isAvailable = listOfAvailableOptions.includes(value);
        const isSoldOut =
          keepSoldOut && !isAvailable && listOfExistingOptions.includes(value);
        const inputIsDisabled = !isAvailable && !isSoldOut;
        const showSoldOut = isSoldOut && hasManyValues;
        const label = showSoldOut ? `${value} - ${soldOutText}` : value;
        const isOption = input.tagName.toLowerCase() === 'option';

        input.disabled = inputIsDisabled;
        if (keepSoldOut) {
          if (isOption) input.textContent = label;
          else input.nextElementSibling?.classList.toggle('is-soldout', showSoldOut);
        }
        if (isOption) {
          const escapedValue = input.value.replace(/"/g, '\\"');
          const selector = `button[data-value="${escapedValue}"]`;
          const dropdown = input.closest('dropdown-input');
          const button = dropdown?.querySelector(selector);
          if (button) {
            button.disabled = inputIsDisabled;
            if (keepSoldOut) button.textContent = label;
          }
          if (keepSoldOut && input.selected) {
            const current = dropdown?.querySelector('[data-label]');
            if (current) current.textContent = label;
          }
        }
        if (!this.productBar) return;
        const inputId = input.dataset.id || input.id;
        const productBarInput = this.productBar.querySelector(
          `option[data-id="${inputId}"]`
        );
        if (!productBarInput) return;
        productBarInput.disabled = inputIsDisabled;
        if (keepSoldOut) productBarInput.textContent = label;
      });

      if (!keepSoldOut) return;
      // Hound-Tees: the selected value has no variant with the options before it (e.g. a size
      // that only comes in another style). Select one that does; the next option reads it.
      const selected = option.querySelector(':checked');
      if (!selected || !selected.disabled) return;
      const replacement =
        optionInputs.find(input =>
          listOfAvailableOptions.includes(input.getAttribute('value'))
        ) || optionInputs.find(input => !input.disabled);
      if (!replacement) return;
      if (replacement.tagName.toLowerCase() === 'option') {
        optionInputs.forEach(input => input.removeAttribute('selected'));
        replacement.setAttribute('selected', 'selected');
        const select = replacement.closest('select');
        if (select) select.value = replacement.value;
        const dropdown = replacement.closest('dropdown-input');
        if (typeof dropdown?.update === 'function') {
          dropdown.update();
        } else {
          const current = dropdown?.querySelector('[data-label]');
          if (current) current.textContent = replacement.textContent;
        }
      } else {
        replacement.checked = true;
        optionInputs.forEach(input =>
          input.nextElementSibling?.classList.toggle('is-active', input.checked)
        );
      }
      if (!this.productBar) return;
      const productBarOption = this.productBar.querySelector(
        `option[data-id="${replacement.dataset.id || replacement.id}"]`
      );
      const productBarSelect = productBarOption?.closest('select');
      if (!productBarSelect) return;
      Array.from(productBarSelect.options).forEach(barOption =>
        barOption.toggleAttribute('selected', barOption === productBarOption)
      );
      productBarSelect.value = productBarOption.value;
    });
  }

  onVariantChange(event) {
    if (event.target.type === 'number') return;
    this.updateOptions();
    if (this.currentVariant === this.getVariantData()) return;

    this.updateVariant();
    this.updateVariantStatuses();
    if (this.hasAttribute('data-show-soldout')) {
      // Hound-Tees: updateVariantStatuses may have moved a later option to a combination that exists.
      this.updateOptions();
      this.updateVariant();
    }
    this.productBarUpdateOptions();
    this.updatePickupAvailability();
    this.form.removeAttribute('data-has-selling-plan');
    this.form.toggleAddButton(false, '');
    this.form.handleErrorMessage();

    if (!this.currentVariant) {
      this.form.toggleAddButton(true, '');
      this.setUnavailable();

      return;
    }

    if (!this.currentVariant.available) {
      ``;
      this.form.toggleAddButton(true, window.variantStrings.soldOut);
    }

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();
    this.renderProductInfo();
  }

  updateOptions() {
    this.querySelectorAll('input[type="radio"]').forEach(input => {
      input.nextElementSibling.classList.toggle(
        'is-active',
        input.checked
      );
    });
    this.options = Array.from(
      this.querySelectorAll('input[type="radio"]:checked, select'),
      el => ({ name: el.dataset.name || el.name, value: el.value })
    );
  }

  productBarUpdateOptions() {
    if (this.productBar) {
      Array.from(this.productBar.querySelectorAll('select')).map(
        selector => {
          selector.value = this.options.find(
            option => option.name === selector.name
          ).value;
        }
      );
      // update dropdowns in product bar
      this.productBar.querySelectorAll('dropdown-input').forEach(
        dropdown => {
          // update dropdown list with usage update() of 'class DropdownInput extends HTMLElement' in global.js
          dropdown.update();
        }
      );
    }
  }

  updateInventoryNotice(html) {
    const destination = document.querySelector('[data-inventory-notice]');
    const source = html.querySelector('[data-inventory-notice]');

    if (source && destination) destination.innerHTML = source.innerHTML;
  }

  updatePickupAvailability() {
    const pickUpAvailabilityNode = document.querySelector(
      'pickup-availability'
    );

    if (!pickUpAvailabilityNode || !this.currentVariant) return;

    pickUpAvailabilityNode.dataset.variantId = this.currentVariant.id;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailabilityNode.setAttribute('available', '');
      pickUpAvailabilityNode.fetchAvailability(
        pickUpAvailabilityNode.dataset.variantId
      );

      return;
    }

    pickUpAvailabilityNode.removeAttribute('available');
    pickUpAvailabilityNode.innerHTML = '';
  }

  updateVariant() {
    this.currentVariant = this.getVariantData();
  }

  getVariantData() {
    return this.options.length
      ? this.variants.find(variant => {
          return !variant.options
            .map(
              (option, index) => this.options[index].value === option
            )
            .includes(false);
        })
      : this.variants[0];
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant.featured_media)
      return;

    const productMedia = document.querySelector('product-media');
    productMedia.setActiveMedia(
      this.currentVariant.featured_media.id
    );
  }

  updateURL(sellingPlanId) {
    if (!this.currentVariant) return;
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    if (sellingPlanId) {
      params[params.has('selling_plan') ? 'set' : 'append'](
        'selling_plan',
        sellingPlanId
      );
    } else {
      params.delete('selling_plan');
    }
    window.history.replaceState(
      {},
      '',
      `${this.dataset.url}?${params.toString()}`
    );
  }

  updateVariantInput() {
    const inputs = [
      this.querySelector('[name="id"'),
      this.installmentsForm?.querySelector('[name="id"]')
    ];
    inputs.forEach(input => {
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  setUnavailable() {
    const submitButton = this.form.submitButton;
    const price = this.querySelector(
      `#Product-Price-${this.dataset.sectionId}`
    );

    if (!submitButton) return;
    submitButton.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  renderProductInfo() {
    const params = new URLSearchParams(window.location.search);
    params[params.has('variant') ? 'set' : 'append'](
      'variant',
      this.currentVariant.id
    );
    params.append('section_id', this.dataset.sectionId);

    fetch(`${this.dataset.url}?${params.toString()}`)
      .then(response => response.text())
      .then(responseText => {
        const html = new DOMParser().parseFromString(
          responseText,
          'text/html'
        );
        [
          `#Product-Price-${this.dataset.sectionId}`,
          `#Product-Purchase-Options-${this.dataset.sectionId}`
        ].map(liveRegionSelector => {
          const destination = document.querySelector(
            liveRegionSelector
          );
          const source = html.querySelector(liveRegionSelector);

          this.updateInventoryNotice(html);

          if (destination && source) {
            destination.classList.remove('visibility-hidden');
            destination.innerHTML = source.innerHTML;
          }
        });
      });
  }
}

customElements.define('product-selector', ProductSelector);
